import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import { attendanceStructureService } from "../../../services/api";

export default function PayrollPendingLeaveModal({
  darkMode,
  rows = [],
  onClose,
  onDone,
  onIgnoreIds,
}) {
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  const allIds = useMemo(
    () => rows.map((r) => r.leaveRequestId).filter(Boolean),
    [rows]
  );

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === allIds.length) setSelected(new Set());
    else setSelected(new Set(allIds));
  };

  const runBulk = async (action) => {
    const ids = selected.size ? [...selected] : allIds;
    if (!ids.length) {
      toast.error("Select at least one leave request");
      return;
    }
    setBusy(true);
    try {
      if (action === "ignore") {
        onIgnoreIds?.(ids);
        toast.success(`Ignored ${ids.length} leave request(s) for payroll check`);
        onDone?.();
        onClose?.();
        return;
      }
      for (const id of ids) {
        if (action === "approve") await attendanceStructureService.approveLeaveRequest(id, "Payroll force approve");
        else await attendanceStructureService.rejectLeaveRequest(id, "Payroll force reject");
      }
      toast.success(`${action === "approve" ? "Approved" : "Rejected"} ${ids.length} request(s)`);
      onDone?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100050] flex items-start justify-center bg-black/50 p-4 pt-20 pb-8"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`flex max-h-[calc(100vh-6rem)] w-full max-w-2xl flex-col overflow-hidden rounded-xl border shadow-xl ${
          darkMode ? "border-[#3C3C3C] bg-[hsl(var(--ds-surface))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
        }`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between border-b px-4 py-3 dark:border-[#3C3C3C]">
          <h3 className="text-sm font-semibold">Pending leave approvals ({rows.length})</h3>
          <button type="button" onClick={onClose} className="rounded p-1 hover:bg-[hsl(var(--ds-muted))] dark:hover:bg-[hsl(var(--ds-muted))]">
            <X size={18} />
          </button>
        </div>
        <div className="flex flex-wrap gap-2 border-b px-4 py-2 dark:border-[#3C3C3C]">
          <button
            type="button"
            disabled={busy || !allIds.length}
            onClick={() => runBulk("approve")}
            className="rounded bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
          >
            Force Approve
          </button>
          <button
            type="button"
            disabled={busy || !allIds.length}
            onClick={() => runBulk("reject")}
            className="rounded bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-xs font-medium text-[hsl(var(--ds-primary-fg))] disabled:opacity-50"
          >
            Force Reject
          </button>
          <button
            type="button"
            disabled={busy || !allIds.length}
            onClick={() => runBulk("ignore")}
            className={`rounded border px-3 py-1.5 text-xs disabled:opacity-50 ${
              darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
            }`}
          >
            Ignore for payroll
          </button>
          <label className="ml-auto flex items-center gap-2 text-xs">
            <input type="checkbox" checked={selected.size === allIds.length && allIds.length > 0} onChange={toggleAll} />
            Select all
          </label>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-2 text-xs">
          {rows.map((row) => (
            <label
              key={row.leaveRequestId || `${row.employeeId}-${row.fromDate}`}
              className={`mb-1 flex cursor-pointer items-center gap-2 rounded border px-3 py-2 ${
                darkMode ? "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]" : "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
              }`}
            >
              <input
                type="checkbox"
                checked={selected.has(row.leaveRequestId)}
                onChange={() => toggle(row.leaveRequestId)}
              />
              <span className="min-w-0 flex-1">
                <strong>{row.empCode}</strong> — {row.name} · {row.leaveType} · {row.fromDate} → {row.toDate}
              </span>
            </label>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}
